import { STORAGE_KEYS } from "constants/index";
import type { User } from "types/user";

class StorageService {
  private storage: Storage;

  constructor(persistent: boolean = true) {
    this.storage = persistent ? window.localStorage : window.sessionStorage;
  }

  get<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Error getting item ${key} from storage:`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      this.storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error setting item ${key} in storage:`, error);
    }
  }

  remove(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error(`Error removing item ${key} from storage:`, error);
    }
  }

  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  has(key: string): boolean {
    return this.storage.getItem(key) !== null;
  }
}

export const appLocalStorage = new StorageService(true);
export const appSessionStorage = new StorageService(false);

const AUTH_GRACE_MS = 10_000;

export const tokenStorage = {
  setTokens: (accessToken: string, refreshToken: string) => {
    appLocalStorage.set(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    appLocalStorage.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    try { sessionStorage.setItem('__token_set_at__', String(Date.now())); } catch {}
    try { localStorage.setItem('__auth_event__', `set:${Date.now()}`); } catch {}
    try { window.dispatchEvent(new CustomEvent('auth:token-changed')); } catch {}
  },

  isRecentlyAuthenticated: (): boolean => {
    try {
      const ts = sessionStorage.getItem('__token_set_at__');
      return !!ts && Date.now() - Number(ts) < AUTH_GRACE_MS;
    } catch { return false; }
  },

  getAccessToken: (): string | null => {
    return appLocalStorage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
  },

  getRefreshToken: (): string | null => {
    return appLocalStorage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  },

  clearTokens: () => {
    appLocalStorage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    appLocalStorage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    // Touch a well-known key to trigger the storage event across tabs
    try { localStorage.setItem('__auth_event__', `clear:${Date.now()}`); } catch {}
    try { window.dispatchEvent(new CustomEvent('auth:token-changed')); } catch {}
  },

  hasValidToken: (): boolean => {
    const token = tokenStorage.getAccessToken();
    if (!token) return false;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return false;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded));
      if (!payload.exp) return true;
      return Date.now() < payload.exp * 1000 - 5000;
    } catch {
      return false;
    }
  },
};

/** Onboarding session when sign-in returns user info without JWT tokens. */
export const registrationSessionStorage = {
  setUser: (user: User) => {
    appSessionStorage.set(STORAGE_KEYS.REGISTRATION_USER, user);
  },

  getUser: (): User | null => {
    return appSessionStorage.get<User>(STORAGE_KEYS.REGISTRATION_USER);
  },

  clear: () => {
    appSessionStorage.remove(STORAGE_KEYS.REGISTRATION_USER);
  },
};

export default StorageService;

